/** Shared helpers for the OpenNebula compute console. */

export type InstanceTone = 'success' | 'danger' | 'warning' | 'muted' | 'info';

/** `state` (BACKUP*) wins over `status` for the displayed key — mirrors @console. */
export function instanceStateKey(state?: string, status?: string): string | undefined {
  return state === 'BACKUP' || state === 'BACKUP_POWEROFF' ? state : status;
}

export function instanceTone(key?: string): InstanceTone {
  // The list endpoint returns lowercase lifecycle ('active'); the per-instance
  // summary returns uppercase OpenNebula states ('POWERED_ON'). Handle both.
  switch (String(key ?? '').toUpperCase()) {
    case 'POWERED_ON':
    case 'ACTIVE':
    case 'RUNNING':
      return 'success';
    case 'SUSPENDED':
    case 'BACKUP':
    case 'BACKUP_POWEROFF':
      return 'warning';
    case 'ERROR':
    case 'FAILED':
      return 'danger';
    case 'POWERED_OFF':
    case 'OFF':
    case 'INACTIVE':
    default:
      return 'muted';
  }
}

/** "POWERED_ON" → "Powered on". */
export function instanceLabel(key?: string | number): string {
  if (key == null || key === '') return 'Unknown';
  const s = String(key).split('_').join(' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Coarse bucket for the header count band. */
export function instanceBucket(key?: string): 'running' | 'stopped' | 'error' | 'other' {
  switch (String(key ?? '').toUpperCase()) {
    case 'POWERED_ON':
    case 'ACTIVE':
    case 'RUNNING':
      return 'running';
    case 'POWERED_OFF':
    case 'OFF':
    case 'INACTIVE':
    case 'SUSPENDED':
      return 'stopped';
    case 'ERROR':
    case 'FAILED':
      return 'error';
    default:
      return 'other';
  }
}

/**
 * Derive the displayable IPs from an instance's NICs, matching how @console
 * reads them: the public IP lives on the NIC whose network name contains
 * "public" (e.g. `public-network-601`), the private IP on a "private" NIC, and
 * the floating IP on a NIC alias. Single-public-NIC instances therefore expose
 * a Public IP (previously mislabeled / shown as "—" because the old code read a
 * non-existent `nic_alias[0]`).
 */
export function instanceIps(metadata?: {
  nics?: { ip?: string; network?: string; dns?: string }[];
  nic_alias?: { ip?: string }[];
}): { publicIp?: string; privateIp?: string; floatingIp?: string; dns?: string } {
  const nics = metadata?.nics ?? [];
  const publicNic = nics.find((n) => /public/i.test(n.network ?? ''));
  const privateNic = nics.find((n) => /private/i.test(n.network ?? ''));
  // Fall back to the first NIC for the private slot when no NIC is explicitly
  // named "private" and it isn't the public one.
  const fallbackPrivate = nics.find((n) => n !== publicNic);
  return {
    publicIp: publicNic?.ip,
    privateIp: (privateNic ?? fallbackPrivate)?.ip,
    floatingIp: metadata?.nic_alias?.[0]?.ip,
    dns: (publicNic ?? nics[0])?.dns,
  };
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
